import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, AppWindowIcon, LayersIcon, ShowerHeadIcon, DoorOpenIcon, ChevronDownIcon } from 'lucide-react';
import { KDesignersQuoteForm } from './KDesignersQuoteForm';
interface KDesignersLandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function KDesignersLandingPage({
  initialService,
  onClose
}: KDesignersLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <KDesignersQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-1613" />;
  }
  const services = [{
    id: 'windows',
    name: 'Windows',
    icon: <AppWindowIcon className="w-6 h-6" data-id="element-1614" />,
    desc: 'Starting at $49/month'
  }, {
    id: 'siding',
    name: 'Siding',
    icon: <LayersIcon className="w-6 h-6" data-id="element-1615" />,
    desc: 'Starting at $79/month'
  }, {
    id: 'bathroom',
    name: 'Bathroom',
    icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-1616" />,
    desc: 'Starting at $69/month'
  }, {
    id: 'doors',
    name: 'Entry Doors',
    icon: <DoorOpenIcon className="w-6 h-6" data-id="element-1617" />,
    desc: 'Starting at $39/month'
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-1618">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-1619">
        <div className="flex items-center gap-6" data-id="element-1620">
          <img src="https://cdn.magicpatterns.com/uploads/idrKUKYXCqVbysWS3F6st9/K_Designers_Logo.png" alt="K-Designers" className="h-14 md:h-16 object-contain" data-id="element-1621" />
          <nav className="hidden md:flex items-center gap-4 text-sm font-semibold text-gray-600" data-id="element-1622">
            <span data-id="element-1623">Windows</span>
            <span className="text-gray-300" data-id="element-1624">&bull;</span>
            <span data-id="element-1625">Siding</span>
            <span className="text-gray-300" data-id="element-1626">&bull;</span>
            <span data-id="element-1627">Bathroom</span>
            <span className="text-gray-300" data-id="element-1628">&bull;</span>
            <span data-id="element-1629">Entry Doors</span>
          </nav>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#1B2A4A] py-4 md:py-6 border-t border-[#121c32]" data-id="element-1630">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1631">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-1632">
            A Great Day Improvements brand
          </h2>
          <p className="text-blue-100 text-sm mt-1" data-id="element-1633">
            Over 140,000 satisfied customers since 1978.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-1634">
        <div className="absolute inset-0 z-0" data-id="element-1635">
          <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80" alt="Beautiful home exterior" className="w-full h-full object-cover" data-id="element-1636" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A4A]/90 via-[#1B2A4A]/60 to-transparent" data-id="element-1637" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1638">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1639">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1640">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-1641">
                A New Home at Your Old Address
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-1642">
                Let us make your Dream Home a Reality. Over 140,000 satisfied
                customers since 1978.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1643">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-1644">
                <h2 className="text-xl font-bold text-[#1B2A4A] text-center mb-6" data-id="element-1645">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select a Service & Enter Your Zip'}
                </h2>

                {/* Service dropdown */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-1646">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-blue-50 ${selectedService ? 'border-[#00B4D8]' : 'border-blue-200'} ${dropdownOpen ? 'border-[#00B4D8] shadow-md' : 'hover:border-blue-300'}`} data-id="element-1647">
                      <div className="flex items-center gap-3" data-id="element-1648">
                        {selectedService ? <>
                            <span className="text-[#00B4D8]" data-id="element-1649">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <div className="flex flex-col text-left" data-id="element-1650">
                              <span className="font-bold text-[#1B2A4A] text-lg leading-tight" data-id="element-1651">
                                {services.find(s => s.id === selectedService)?.name}
                              </span>
                              <span className="text-xs text-gray-500" data-id="element-1652">
                                {services.find(s => s.id === selectedService)?.desc}
                              </span>
                            </div>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-1653">
                            Choose a service...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#00B4D8] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-1654" />
                    </button>

                    <AnimatePresence data-id="element-1655">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-blue-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-1656">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-blue-50 text-[#1B2A4A]' : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-[#1B2A4A]'} border-b border-blue-100 last:border-b-0`} data-id="element-1657">
                              <span className={`${selectedService === svc.id ? 'text-[#00B4D8]' : 'text-gray-400'}`} data-id="element-1658">
                                {svc.icon}
                              </span>
                              <div className="flex flex-col" data-id="element-1659">
                                <span className="font-bold text-lg leading-tight" data-id="element-1660">
                                  {svc.name}
                                </span>
                                <span className="text-xs text-gray-500" data-id="element-1661">
                                  {svc.desc}
                                </span>
                              </div>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#00B4D8] ml-auto" data-id="element-1662" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected service badge when pre-selected */}
                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-id="element-1663">
                    {services.find(s => s.id === initialService)?.icon}
                    <span className="font-bold text-[#1B2A4A]" data-id="element-1664">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-1665">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-colors" maxLength={5} data-id="element-1666" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-1667">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-1668" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-1669">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-1670">
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-1671">
                    <ShieldCheckIcon className="w-5 h-5 text-[#00B4D8]" data-id="element-1672" />
                    <span className="text-xs font-bold text-[#1B2A4A] uppercase tracking-tight" data-id="element-1673">
                      Lifetime Warranty
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200" data-id="element-1674">
                    <div className="font-black text-slate-700 text-sm" data-id="element-1675">BBB</div>
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-tight" data-id="element-1676">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Teal Accent Bar */}
      <div className="w-full h-3 bg-[#00B4D8]" data-id="element-1677" />

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-1678">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1679">
          <div className="text-center mb-16" data-id="element-1680">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A]" data-id="element-1681">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-1682">
            {[{
            title: 'Choose Your Project',
            desc: 'Select from windows, siding, bathroom remodeling, or entry doors. We offer custom designs for your New Dream Home.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Get Your Free Estimate',
            desc: 'A K-Designers representative will visit your home, show you product samples, and provide a no-obligation quote with flexible financing options.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Professional Installation',
            desc: 'Our factory-trained installers handle everything from start to finish, backed by our lifetime warranty for your peace of mind.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-1683">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-1684">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-1685" />
                </div>
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-4" data-id="element-1686">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1687">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B2A4A] py-16" data-id="element-1688">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1689">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1690">
            <div className="flex flex-col gap-8" data-id="element-1691">
              <div className="flex flex-col" data-id="element-1692">
                <img src="https://cdn.magicpatterns.com/uploads/idrKUKYXCqVbysWS3F6st9/K_Designers_Logo.png" alt="K-Designers" className="h-12 object-contain self-start bg-white px-3 py-1.5 rounded-lg mb-2" data-id="element-1693" />
                <span className="text-[10px] font-semibold text-[#00B4D8]" data-id="element-1694">
                  Windows &bull; Siding &bull; Bathroom &bull; Entry Doors
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-1695">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1696">
              <div className="flex flex-col gap-4" data-id="element-1697">
                <a href="#" className="text-xs text-white hover:text-[#00B4D8] transition-colors font-medium" data-id="element-1698">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:text-[#00B4D8] transition-colors font-medium" data-id="element-1699">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:text-[#00B4D8] transition-colors font-medium" data-id="element-1700">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-1701">
                <a href="#" className="text-xs text-white hover:text-[#00B4D8] transition-colors font-medium" data-id="element-1702">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:text-[#00B4D8] transition-colors font-medium" data-id="element-1703">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-1704">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-1705">
              K-Designers services are available in select markets. All
              estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}